// Module 3.2b — Drill String Editor.
//
// Sections:
//   1. Identity (name, DS #, revision, run #, BHA reference picker)
//   2. Status (assembly + operational)
//   3. Operating window
//   4. Sections — ordered table, reorder up/down, Module 2 library picker per
//      row (drill_pipe / hwdp / crossover), live per-row geometry preview.
//   5. Live Summary Totals — Total Length / Weight / Internal Capacity /
//      Joint Count / Referenced BHA (sticky-feel strip).
//   6. Notes.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import {
  ArrowLeft, Save, AlertTriangle, Plus, Trash2, Library,
  ArrowUp, ArrowDown, Layers, Trash,
} from "lucide-react";
import { Field, TextInput, TextArea } from "@/components/ReportPrimitives";
import FloatingActionToolbar from "@/components/FloatingActionToolbar";
import UnsavedChangesDialog from "@/components/UnsavedChangesDialog";
import useUnsavedChanges from "@/hooks/useUnsavedChanges";
import {
  emptyDrillStringRecord, emptyDrillStringSection,
  computeDrillStringGeometry, validateDrillStringRecord,
  dsRoleRefCategory, dsRoleLabel,
  DRILLSTRING_SECTION_ROLES,
  DS_ASSEMBLY_STATUSES, DS_OPERATIONAL_STATUSES,
} from "@/lib/drillstring/schema";
import {
  createDrillString, getDrillString, updateDrillString, deleteDrillString,
} from "@/lib/drillstring/storage";
import { getWellProfile } from "@/lib/well/storage";
import { wellStackMode } from "@/lib/well/schema";
import { useStackMode } from "@/lib/stackModeContext";
import { listBhasForWell } from "@/lib/bha/storage";
import { listByCategory, findById } from "@/lib/reflibs/lookup";
import { fillBlanksFromLibrary, applyAllFromLibrary, libraryConflicts } from "@/lib/reflibs/libraryFill";
import { libraryReturnState, getRestoredDraft, getRestoredAnchor, scrollToRestoredAnchor } from "@/lib/reflibs/returnContext";
import RceEditorStrip from "@/components/rce/RceEditorStrip";
import { nextWellSectionHash } from "@/lib/well/completion";

function NativeSelect({ value, onChange, options, testId, disabled }) {
  return (
    <select value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            data-testid={testId}
            className="input-rs">
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
const NumIn  = (p) => <TextInput type="number" {...p} />;
// Five-Defect Correction Pass, Defect 4 — see BhaEditor.jsx for rationale
// (identical component, kept local to each file per existing convention).
const DateIn = ({ value, onChange, testId }) => {
  const [datePart, timePart] = String(value || "").split("T");
  return (
    <div className="flex gap-2">
      <input type="date" value={datePart || ""}
             onChange={(e) => onChange(e.target.value ? `${e.target.value}T${timePart || "00:00"}` : "")}
             data-testid={`${testId}-date`} className="input-rs flex-1" />
      <input type="time" value={timePart || ""} disabled={!datePart}
             onChange={(e) => onChange(`${datePart}T${e.target.value || "00:00"}`)}
             data-testid={`${testId}-time`} className="input-rs flex-1" />
    </div>
  );
};

function _refLabel(r) {
  if (!r) return "(missing — record may have been removed)";
  return [r.nominalSize, r.grade, r.connection, r.nominalWeightPpf && `${r.nominalWeightPpf} ppf`]
    .filter(Boolean).join(" · ") || r.id;
}

function _libraryMapped(r) {
  if (!r) return {};
  return {
    outerDiameter: r.outerDiameter ?? "",
    innerDiameter: r.innerDiameter ?? "",
    // T0 — field-name normalisation. v2 library records store the canonical
    // key `weight` (lb/ft); the previous read on `nominalWeightPpf`/`weightPpf`
    // never matched and so the section's Weight cell never auto-filled.
    weightPpf:     r.weight ?? "",
    grade:         r.grade ?? "",
    connection:    r.connection ?? "",
  };
}

// Picking a record fills BLANK fields only — a value the operator entered is
// never replaced automatically. Clearing the picker unlinks the reference and
// keeps every entered dimension as standalone section data.
function _populateFromLibrary(sec, refId) {
  const prevRef = sec.refId ? findById(sec.refId) : null;
  const prevLabel = prevRef ? _refLabel(prevRef) : "";
  if (!refId) {
    const wasLibraryName = sec.displayName && sec.displayName === prevLabel;
    return { ...sec, refId: "", displayName: wasLibraryName ? "" : sec.displayName };
  }
  const r = findById(refId);
  if (!r) return { ...sec, refId };
  const out = fillBlanksFromLibrary({ ...sec, refId }, _libraryMapped(r));
  if (!sec.displayName || sec.displayName === prevLabel) out.displayName = _refLabel(r);
  return out;
}

// Explicit operator action — replaces populated fields with the library values.
function _applyLibraryValues(sec) {
  const r = sec.refId ? findById(sec.refId) : null;
  if (!r) return sec;
  return { ...applyAllFromLibrary(sec, _libraryMapped(r)), displayName: _refLabel(r) };
}

function _sectionLibraryConflicts(sec) {
  const r = sec.refId ? findById(sec.refId) : null;
  if (!r) return [];
  return libraryConflicts(sec, _libraryMapped(r));
}

const _fmt = (v, digits = 2) => {
  if (v === "" || v == null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
};

// =============================================================================
export default function DrillStringEditor() {
  const { stackMode } = useStackMode();
  const navigate = useNavigate();
  const location = useLocation();
  const { wellId, dsId } = useParams();
  const isNew = !dsId;
  const well  = useMemo(() => getWellProfile(wellId), [wellId]);
  const bhas  = useMemo(() => listBhasForWell(wellId), [wellId]);

  const [rec, setRec]       = useState(() => ({ ...emptyDrillStringRecord(), wellId }));
  const [errors, setErrors] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (isNew) {
      setRec({ ...emptyDrillStringRecord(), wellId });
      setNotFound(false);
      return;
    }
    const row = getDrillString(dsId);
    if (row && row.wellId === wellId) { setRec(row); setNotFound(false); }
    else { setNotFound(true); }
  }, [dsId, wellId, isNew]);

  // Reference Library Return-Workflow — restore in-progress edits that
  // survived a round trip to a Reference Library. Consumed once, then
  // cleared from history state so a later refresh never re-applies it.
  useEffect(() => {
    const draft = getRestoredDraft(location);
    if (!draft) return;
    const matches = isNew ? (!draft.id && draft.wellId === wellId) : (draft.id === dsId);
    if (matches) setRec(draft);
    scrollToRestoredAnchor(getRestoredAnchor(location));
    navigate(location.pathname + location.search, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const baked = useMemo(() => computeDrillStringGeometry(rec), [rec]);
  const locked = rec.assemblyStatus === "locked";

  const set = (patch) => setRec((cur) => ({ ...cur, ...patch }));

  // RC-1 · Unsaved Changes Protection (presentation only).
  const unsaved = useUnsavedChanges({ data: rec, enabled: !notFound, onSave: () => handleSave() });
  const _setSecs = (next) => set({ sections: next });

  const addSection = (role = "drill_pipe") => {
    _setSecs([...(rec.sections || []), emptyDrillStringSection(role)]);
  };
  const removeSection = (idx) => {
    const next = [...(rec.sections || [])]; next.splice(idx, 1); _setSecs(next);
  };
  const patchSection = (idx, patch) => {
    const next = [...(rec.sections || [])]; next[idx] = { ...next[idx], ...patch }; _setSecs(next);
  };
  const move = (idx, dir) => {
    const next = [...(rec.sections || [])];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    _setSecs(next);
  };
  const changeRole = (idx, newRole) => {
    patchSection(idx, {
      role: newRole, refCategory: dsRoleRefCategory(newRole),
      refId: "", displayName: "",
    });
  };

  const handleSave = () => {
    const v = validateDrillStringRecord(rec);
    // Explicit `false` — useUnsavedChanges tests `result === false` to keep
    // the Save-and-Leave dialog open; a bare `return` reads as success.
    if (!v.ok) { setErrors(v.errors); window.scrollTo({ top: 0, behavior: "smooth" }); return false; }
    try {
      const saved = isNew ? createDrillString(rec) : updateDrillString(dsId, rec);
      setErrors([]);
      unsaved.markSaved(saved || rec);
      // RC-1 Commercial Workflow QA — return to Well Profile workbook if
      // the operator arrived from there.
      const returnToWell = new URLSearchParams(window.location.search).get("return") === "well";
      if (returnToWell) {
        navigate(nextWellSectionHash(wellId, "drillstring"));
      } else {
        navigate(`/wells/${wellId}/drill-strings/${saved.id}`);
      }
      return true;
    } catch (e) {
      setErrors(e.validationErrors || [e.message || "Save failed."]);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    }
  };
  const handleDelete = () => {
    if (!dsId) return;
    if (!window.confirm(`Delete drill string "${rec.name}"?`)) return;
    deleteDrillString(dsId);
    navigate(`/wells/${wellId}/drill-strings`);
  };

  if (!well) {
    return (
      <div className="card-rs p-6 text-sm text-[var(--rs-text-dim,#9aa6b2)]">
        Well not found. <Link to="/wells" className="text-amber-400">Back to Wells</Link>.
      </div>
    );
  }

  // Surface/Subsea isolation — see KillSheetOpHub for rationale.
  if (wellStackMode(well) !== stackMode) {
    return (
      <div className="card-rs p-6 text-sm text-[var(--rs-text-dim,#9aa6b2)]">
        &quot;{well.wellName || "This well"}&quot; belongs to {wellStackMode(well) === "subsea" ? "Subsea" : "Surface"} mode.
        Switch the operating-mode toggle back to view its drill strings, or{" "}
        <Link to="/wells" className="text-amber-400">go back to Wells</Link>.
      </div>
    );
  }
  if (notFound) {
    return (
      <div className="card-rs p-6 text-sm text-[var(--rs-text-dim,#9aa6b2)]">
        Drill string not found for this well.{" "}
        <Link to={`/wells/${wellId}/drill-strings`} className="text-amber-400">Back to Drill Strings</Link>.
      </div>
    );
  }

  const referencedBha = bhas.find((b) => b.id === rec.bhaId);

  return (
    <div data-testid="ds-editor" className="flex flex-col gap-4 pb-8">
      <header className="flex items-center justify-between gap-3">
        <button type="button"
                onClick={() => unsaved.guardedNavigate(`/wells/${wellId}/drill-strings`)}
                data-testid="ds-editor-back"
                className="inline-flex items-center gap-1.5 text-[12px] text-[var(--rs-text-2)] hover:text-[var(--rs-amber)]">
          <ArrowLeft className="w-3.5 h-3.5" /> Drill Strings
        </button>
        <div className="flex items-center gap-2">
          {!isNew && (
            <button type="button" onClick={handleDelete}
                    data-testid="ds-editor-delete"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] text-red-300 border border-red-500/30 hover:bg-red-500/10">
              <Trash className="w-3.5 h-3.5" /> Delete
            </button>
          )}
        </div>
      </header>

      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--rs-text-dim,#9aa6b2)]">
          Module 3.2b · {well.wellName || "Well"} · Drill String
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold mt-0.5 text-[var(--rs-text,#e8edf2)] inline-flex items-center gap-2">
          <Layers className="w-5 h-5 text-amber-400" />
          {isNew ? "New Drill String" : (rec.name || "(unnamed drill string)")}
          {rec.revision && (
            <span className="ml-1 text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded border border-zinc-500/40 text-zinc-300 align-middle">
              Rev {rec.revision}
            </span>
          )}
        </h1>
      </div>

      <RceEditorStrip
        ownerKey="drillString"
        ownerLabel="Drill String · Engineering Context"
        consumes={["project.operator", "rig.name", "well.name", "bha.id"]}
      />

      {errors.length > 0 && (
        <div data-testid="ds-editor-errors"
             className="card-rs p-3 border-red-500/30 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
          <ul className="text-xs text-red-300 list-disc list-inside space-y-0.5">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {/* Live Summary Totals — always visible near the top so the user sees
          the impact of every edit immediately. */}
      <LiveSummary summary={baked.summary} referencedBha={referencedBha} />

      {/* 1. Identity ------------------------------------------------------ */}
      <section className="card-rs p-4">
        <h2 className="text-[13px] font-semibold mb-3">Identity</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Drill string name (required)">
            <TextInput value={rec.name} onChange={(v) => set({ name: v })} testId="ds-name" />
          </Field>
          <Field label="String number">
            <TextInput value={rec.stringNumber} onChange={(v) => set({ stringNumber: v })} testId="ds-stringNumber" />
          </Field>
          <Field label="Revision (optional)" helper={"e.g. A, B, C — track small changes without a new record."}>
            <TextInput value={rec.revision} onChange={(v) => set({ revision: v })} testId="ds-revision" />
          </Field>
          <Field label="Run number">
            <TextInput value={rec.runNumber} onChange={(v) => set({ runNumber: v })} testId="ds-runNumber" />
          </Field>
          <Field label="Referenced BHA"
                 helper="Pick which BHA this drill string hangs on. ID is stored — no payload duplicated.">
            <NativeSelect value={rec.bhaId} onChange={(v) => set({ bhaId: v })}
                          testId="ds-bhaId"
                          options={[
                            { value: "", label: "— None selected —" },
                            ...bhas.map((b) => ({
                              value: b.id,
                              label: [b.name, b.revision && `Rev ${b.revision}`, b.operationalStatus]
                                .filter(Boolean).join(" · "),
                            })),
                          ]} />
          </Field>
        </div>
      </section>

      {/* 2. Status ------------------------------------------------------- */}
      <section className="card-rs p-4">
        <h2 className="text-[13px] font-semibold mb-3">Status</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Assembly status">
            <NativeSelect value={rec.assemblyStatus} onChange={(v) => set({ assemblyStatus: v })}
                          testId="ds-assemblyStatus"
                          options={DS_ASSEMBLY_STATUSES.map((s) => ({ value: s, label: s }))} />
          </Field>
          <Field label="Operational status">
            <NativeSelect value={rec.operationalStatus} onChange={(v) => set({ operationalStatus: v })}
                          testId="ds-operationalStatus"
                          options={DS_OPERATIONAL_STATUSES.map((s) => ({ value: s, label: s }))} />
          </Field>
        </div>
      </section>

      {/* 3. Operating window -------------------------------------------- */}
      <section className="card-rs p-4">
        <h2 className="text-[13px] font-semibold mb-3">Operating Window</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Start (date / time)">
            <DateIn value={rec.startDateTime} onChange={(v) => set({ startDateTime: v })}
                    testId="ds-startDateTime" />
          </Field>
          <Field label="End (date / time)">
            <DateIn value={rec.endDateTime} onChange={(v) => set({ endDateTime: v })}
                    testId="ds-endDateTime" />
          </Field>
          <Field label="Depth in (ft)">
            <NumIn value={rec.depthInFt} onChange={(v) => set({ depthInFt: v })} testId="ds-depthInFt" />
          </Field>
          <Field label="Depth out (ft)">
            <NumIn value={rec.depthOutFt} onChange={(v) => set({ depthOutFt: v })} testId="ds-depthOutFt" />
          </Field>
        </div>
      </section>

      {/* 4. Sections ----------------------------------------------------- */}
      <section className="card-rs p-4">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div>
            <h2 className="text-[13px] font-semibold">Sections</h2>
            <p className="text-[10.5px] text-[var(--rs-text-dim,#9aa6b2)] mt-0.5">
              Ordered <strong>bit-end outward</strong> — Section #1 sits on top of the BHA.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {DRILLSTRING_SECTION_ROLES.map((r) => (
              <button key={r.role} type="button"
                      onClick={() => addSection(r.role)}
                      data-testid={`ds-add-${r.role}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10.5px] font-medium border border-amber-500/40 text-amber-400 hover:bg-amber-500/10">
                <Plus className="w-3 h-3" /> {r.label}
              </button>
            ))}
          </div>
        </div>

        {(rec.sections?.length || 0) === 0 ? (
          <div className="text-xs text-[var(--rs-text-dim,#9aa6b2)] italic">
            No sections yet. Tap a section button above to add one.
          </div>
        ) : (
          <div className="flex flex-col gap-3" data-testid="ds-section-rows">
            {baked.sections.map((s, idx) => (
              <SectionRow key={s.sectionUid || idx}
                          idx={idx}
                          section={s}
                          onChange={(p) => patchSection(idx, p)}
                          onRemove={() => removeSection(idx)}
                          onRoleChange={(r) => changeRole(idx, r)}
                          onMove={(d) => move(idx, d)}
                          libraryState={libraryReturnState(location, rec, `ds-section-${idx}`)} />
            ))}
          </div>
        )}

        {/* RC-1 — bottom-of-list Add buttons so the user never scrolls up. */}
        {(rec.sections?.length || 0) > 0 && (
          <div className="mt-4 pt-3 border-t border-[var(--rs-border,#1f2630)] flex flex-wrap items-center justify-center gap-2">
            {DRILLSTRING_SECTION_ROLES.map((r) => (
              <button key={`bottom-${r.role}`} type="button"
                      onClick={() => addSection(r.role)}
                      data-testid={`ds-add-bottom-${r.role}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11.5px] font-medium border border-amber-500/40 text-amber-400 hover:bg-amber-500/10">
                <Plus className="w-3.5 h-3.5" /> {r.label}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* 5. Notes -------------------------------------------------------- */}
      <section className="card-rs p-4">
        <h2 className="text-[13px] font-semibold mb-3">Notes</h2>
        <Field label="Free-text notes">
          <TextArea value={rec.notes} onChange={(v) => set({ notes: v })}
                    testId="ds-notes" rows={3} />
        </Field>
      </section>

      {/* RC-1 Editor Save Consistency — primary Save anchored at the bottom. */}
      <div data-testid="ds-editor-bottom-bar"
           className="card-rs p-4 flex flex-wrap items-center justify-end gap-2">
        <button type="button"
                onClick={() => unsaved.guardedNavigate(`/wells/${wellId}/drill-strings`)}
                data-testid="ds-editor-back-bottom"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-medium text-[var(--rs-text-2)] border border-[var(--rs-border,#1f2630)] hover:bg-[var(--rs-surface-2,#12161d)] order-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <button type="button" onClick={handleSave}
                data-testid="ds-editor-save"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-semibold order-1"
                style={{ background: "var(--rs-amber, #f5b400)", color: "#0A0B0E" }}>
          <Save className="w-4 h-4" /> Save Drill String
        </button>
      </div>

      <UnsavedChangesDialog
        open={unsaved.dialogOpen}
        onSaveAndLeave={unsaved.handleSaveAndLeave}
        onDiscard={unsaved.handleDiscard}
        onCancel={unsaved.handleCancel}
      />

      {/* RC-1 — Floating Engineering Toolbar */}
      <FloatingActionToolbar
        testId="ds-fab"
        actions={[
          { id: "add",  icon: Plus, label: "Add section",
            onClick: () => addSection(DRILLSTRING_SECTION_ROLES[0]?.role || "drill_pipe"),
            testId: "fab-ds-add" },
          { id: "save", icon: Save, label: "Save", onClick: handleSave, testId: "fab-ds-save", primary: true },
        ]}
      />
    </div>
  );
}

// =============================================================================
// Live Summary Totals strip
// =============================================================================
function LiveSummary({ summary, referencedBha }) {
  return (
    <section data-testid="ds-summary"
             className="card-rs p-3 sm:p-4">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <h2 className="text-[12px] font-semibold tracking-wide uppercase text-[var(--rs-text-2,#c2cad4)]">
          Live Summary Totals
        </h2>
        <span className="text-[9.5px] uppercase tracking-[0.18em] text-[var(--rs-text-3,#6b7785)]">
          Recomputed on every edit
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        <SummaryCell label="Total Length"            value={summary.totalLengthFt}            unit="ft"   digits={0} testId="ds-sum-totalLength" />
        <SummaryCell label="Total Weight"            value={summary.totalWeightLbs}           unit="lbs"  digits={0} testId="ds-sum-totalWeight" />
        <SummaryCell label="Total Internal Capacity" value={summary.totalInternalVolumeBbl}   unit="bbl"  digits={2} testId="ds-sum-totalIntVol" />
        <SummaryCell label="Total Joint Count"       value={summary.totalJointCount}          unit=""     digits={0} testId="ds-sum-totalJoints" />
        <SummaryCell label="Referenced BHA"
                     value={referencedBha
                       ? [referencedBha.name, referencedBha.revision && `Rev ${referencedBha.revision}`]
                           .filter(Boolean).join(" · ")
                       : ""}
                     unit="" isText
                     testId="ds-sum-bha" />
      </div>
    </section>
  );
}

function SummaryCell({ label, value, unit, digits = 2, isText, testId }) {
  let display;
  if (isText) {
    display = (value && String(value).trim()) || "—";
  } else if (value === "" || value == null || !Number.isFinite(Number(value))) {
    display = "—";
  } else {
    display = `${Number(value).toLocaleString(undefined, { maximumFractionDigits: digits })}${unit ? ` ${unit}` : ""}`;
  }
  return (
    <div data-testid={testId}
         className="rounded-md border border-amber-500/20 bg-[rgba(245,180,0,0.04)] p-2.5">
      <div className="text-[9px] uppercase tracking-[0.16em] text-amber-400/80">{label}</div>
      <div className="mt-1 text-[13px] font-semibold text-amber-100 font-mono truncate" title={String(display)}>
        {display}
      </div>
    </div>
  );
}

// =============================================================================
// Section row
// =============================================================================
function SectionRow({ idx, section, onChange, onRemove, onRoleChange, onMove, libraryState }) {
  const refCategory = dsRoleRefCategory(section.role);
  const libOptions = useMemo(() => {
    if (!refCategory) return [];
    const all = listByCategory(refCategory);
    return [{ value: "", label: "— Pick from library —" },
            ...all.map((r) => ({ value: r.id, label: _refLabel(r) }))];
  }, [refCategory]);

  const handleRefId = (refId) => onChange(_populateFromLibrary(section, refId));
  const conflicts = _sectionLibraryConflicts(section);

  return (
    <div id={`ds-section-${idx}`}
         data-testid={`ds-section-${idx}`}
         className="rounded-md border border-[var(--rs-border,#1f2630)] p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="inline-flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.14em] text-[var(--rs-text-dim,#9aa6b2)]">
            Section #{idx + 1}
          </span>
          <select value={section.role}
                  onChange={(e) => onRoleChange(e.target.value)}
                  data-testid={`ds-section-${idx}-role`}
                  className="input-rs !py-0.5 !text-[11.5px]">
            {DRILLSTRING_SECTION_ROLES.map((r) =>
              <option key={r.role} value={r.role}>{r.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => onMove(-1)}
                  data-testid={`ds-section-${idx}-up`}
                  aria-label={`Move section ${idx + 1} up`}
                  className="p-1 rounded text-zinc-400 hover:text-amber-400 hover:bg-amber-500/10">
            <ArrowUp className="w-3.5 h-3.5" />
          </button>
          <button type="button" onClick={() => onMove(1)}
                  data-testid={`ds-section-${idx}-down`}
                  aria-label={`Move section ${idx + 1} down`}
                  className="p-1 rounded text-zinc-400 hover:text-amber-400 hover:bg-amber-500/10">
            <ArrowDown className="w-3.5 h-3.5" />
          </button>
          <button type="button" onClick={onRemove}
                  data-testid={`ds-section-${idx}-remove`}
                  aria-label={`Remove section ${idx + 1}`}
                  className="p-1 rounded text-zinc-400 hover:text-red-400 hover:bg-red-500/10">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {refCategory && (
        <div className="mb-2">
          <Field label="Library record"
                 helper={`Optional. Fills blank fields only — your entered values are never replaced.`}>
            <NativeSelect value={section.refId}
                          onChange={handleRefId}
                          testId={`ds-section-${idx}-refId`}
                          options={libOptions} />
          </Field>
          {conflicts.length > 0 && (
            <div data-testid={`ds-section-${idx}-libconflict`}
                 className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-2">
              <div className="text-[10.5px] text-amber-300">
                Your entered values differ from the linked library record and were kept:{" "}
                {conflicts.map((c) => `${c.label} ${c.current} (library ${c.library})`).join(" · ")}
              </div>
              <button type="button" onClick={() => onChange(_applyLibraryValues(section))}
                      data-testid={`ds-section-${idx}-applylib`}
                      className="mt-1.5 inline-flex items-center gap-1 px-2 py-1 rounded text-[10.5px] font-medium border border-amber-500/40 text-amber-400 hover:bg-amber-500/10">
                Apply library values
              </button>
            </div>
          )}
          <div className="mt-1 text-[10px] text-right">
            <Link to={`/reference-libraries/${refCategory}`}
                  state={libraryState}
                  data-testid={`ds-section-${idx}-managelib`}
                  className="inline-flex items-center gap-1 text-amber-400">
              <Library className="w-3 h-3" /> Manage {dsRoleLabel(section.role)} Library
            </Link>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Field label="OD (in)">
          <NumIn value={section.outerDiameter}
                 onChange={(v) => onChange({ outerDiameter: v })}
                 testId={`ds-section-${idx}-od`} />
        </Field>
        <Field label="ID (in)">
          <NumIn value={section.innerDiameter}
                 onChange={(v) => onChange({ innerDiameter: v })}
                 testId={`ds-section-${idx}-id`} />
        </Field>
        <Field label="Weight (ppf)">
          <NumIn value={section.weightPpf}
                 onChange={(v) => onChange({ weightPpf: v })}
                 testId={`ds-section-${idx}-weight`} />
        </Field>
        <Field label="Grade">
          <TextInput value={section.grade}
                     onChange={(v) => onChange({ grade: v })}
                     testId={`ds-section-${idx}-grade`} />
        </Field>
        <Field label="Connection">
          <TextInput value={section.connection}
                     onChange={(v) => onChange({ connection: v })}
                     testId={`ds-section-${idx}-conn`} />
        </Field>
        <Field label="Joint count">
          <NumIn value={section.jointCount}
                 onChange={(v) => onChange({ jointCount: v })}
                 testId={`ds-section-${idx}-jointCount`} />
        </Field>
        <Field label="Nominal joint length (ft)" helper="Typical Range II = 31.5 ft.">
          <NumIn value={section.nominalJointLengthFt}
                 onChange={(v) => onChange({ nominalJointLengthFt: v })}
                 testId={`ds-section-${idx}-nominalJointLen`} />
        </Field>
        <Field label="Length override (ft)" helper="If set, overrides joint count × nominal length.">
          <NumIn value={section.lengthFt}
                 onChange={(v) => onChange({ lengthFt: v })}
                 testId={`ds-section-${idx}-lengthFt`} />
        </Field>
      </div>

      {/* Per-row computed strip */}
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2"
           data-testid={`ds-section-${idx}-computed`}>
        <ComputedCell label="Effective Length"     value={section.effectiveLengthFt}        unit="ft"  digits={0} />
        <ComputedCell label="Capacity"             value={section.internalCapacityBblPerFt} unit="bbl/ft" digits={5} />
        <ComputedCell label="Internal Volume"      value={section.internalVolumeBbl}        unit="bbl" digits={2} />
        <ComputedCell label="Cumulative from BHA"  value={section.cumulativeLengthFt}       unit="ft"  digits={0} />
      </div>
    </div>
  );
}

function ComputedCell({ label, value, unit, digits = 2 }) {
  return (
    <div className="rounded-md border border-[var(--rs-border,#1f2630)] bg-[rgba(255,255,255,0.015)] p-2">
      <div className="text-[9px] uppercase tracking-[0.16em] text-[var(--rs-text-3,#6b7785)]">{label}</div>
      <div className="mt-0.5 text-[11px] font-mono text-[var(--rs-text-2,#c2cad4)] truncate">
        {_fmt(value, digits)} {Number.isFinite(Number(value)) && unit ? <span className="text-[var(--rs-text-3,#6b7785)] text-[9px]">{unit}</span> : null}
      </div>
    </div>
  );
}
